import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import useSummaryData from "../hooks/useSummaryData";
import styled from "styled-components";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  HttpRequestStatus,
  LoadingContainer,
} from "../components/loadingContainer";
import ContainerHeader from "../components/containerHeader";
import SummaryAccordion, { AccordionRow } from "../components/summaryAccordion";
import useEcomodClasses from "../hooks/useEcomodClasses";
import { useUIStore } from "../stores/ui-store";
import { useShallow } from "zustand/shallow";
import { UIState } from "../types/ui-types";
import useSupClasses from "../hooks/useSupClasses";
import usePrices from "../hooks/usePrices";
import useReports from "../hooks/useReports";
import {
  createSummaryData,
  getMatchingReport,
  getReportingCycle,
} from "../utils/summaryUtils";
import { Report, ReportStatus } from "../api/models/report.models";
import TitleComponent from "../components/title";
import { Button } from "primereact/button";
import useApproveReports from "../hooks/useApproveReports";
import GoBackLink from "../components/goBackLink";

const PaddedContainer = styled.div`
  padding: 6rem 3.8rem;
  text-align: left;
  flex-grow: 1;
`;

const ReportContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  height: 100%;
  width: 100%;
`;

const StickyActions = styled.div`
  display: flex;
  justify-content: flex-end;
  position: sticky;
  bottom: 0;
  background-color: #fff;
  z-index: 100;
  box-shadow: 0 -2px 5px rgba(0, 0, 0, 0.1);
  padding: 1rem 2rem;

  & .p-button {
    margin-right: 3rem;
  }
`;

const TotalCostContainer = styled.div`
  margin-top: 2rem;
  padding-right: 1rem;
  font-size: 18px;
  font-weight: 600;
  width: 100%;
  text-align: right;
  display: flex;
  flex-direction: column;

  & .notification {
    font-size: 14px;
    color: var(--text-color-secondary);
    font-weight: 500;
    margin-top: 0.5rem;
  }
`;

const CombinedReportView: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Extract filter parameters from URL
  const year = searchParams.get("year")
    ? parseInt(searchParams.get("year")!, 10)
    : undefined;
  const quarter =
    searchParams.get("quarter") && searchParams.get("quarter") !== "null"
      ? parseInt(searchParams.get("quarter")!, 10)
      : undefined;
  const month =
    searchParams.get("month") && searchParams.get("month") !== "null"
      ? parseInt(searchParams.get("month")!, 10)
      : undefined;
  const reportStatus =
    searchParams.get("status") && searchParams.get("status") !== "null"
      ? parseInt(searchParams.get("status")!, 10)
      : undefined;

  const [locale, companyId, organizationId, companyName] = useUIStore(
    useShallow((state: UIState) => [
      state.locale,
      state.companyId,
      state.organizationId,
      state.companyName,
    ]),
  );

  const { reportsData, isLoadingReports } = useReports({
    companyId,
    organizationId,
    year,
    quarter,
    month,
    reportStatus,
  });

  const {
    reportsData: prevYearReportsData,
    isLoadingReports: isLoadingPrevYearReports,
  } = useReports({
    companyId,
    organizationId,
    year: year ? year - 1 : undefined,
  });

  const combineReports = (reports?: Report[]): Report | undefined => {
    if (!reports || reports.length === 0) {
      return undefined;
    }

    const filteredReports = reports?.filter((report) => {
      if (!quarter && report.quarter) {
        return false;
      }
      if (!month && report.month) {
        return false;
      }

      return true;
    });

    // TO DO: DO STUFF HERE TO COMBINE REPORTS
    const combinedReport: Report = { ...reports[0], rows: [] };
    filteredReports.forEach((report) => {
      combinedReport.rows.push(...report.rows);
    });
    return combinedReport;
  };

  const reportData = useMemo(() => combineReports(reportsData), [reportsData]);
  const prevYearReport = useMemo(() => {
    if (!reportsData || !prevYearReportsData) return undefined;

    const filteredCurrentReports = reportsData.filter((report) => {
      if (!quarter && report.quarter) return false;
      if (!month && report.month) return false;
      return true;
    });

    const matchedPrevReports = filteredCurrentReports
      .map((currentReport) => {
        const orgPrevReports = prevYearReportsData.filter(
          (r) => r.organizationId === currentReport.organizationId,
        );
        return getMatchingReport(orgPrevReports, currentReport);
      })
      .filter((r): r is Report => r !== null);

    if (matchedPrevReports.length === 0) return undefined;
    const combinedReport: Report = { ...matchedPrevReports[0], rows: [] };
    matchedPrevReports.forEach((report) => {
      combinedReport.rows.push(...report.rows);
    });
    return combinedReport;
  }, [reportsData, prevYearReportsData, quarter, month]);

  const { pricesData, isLoading: isLoadingPrices } = usePrices({
    year: year ? year : new Date().getFullYear(),
  });

  const { isApproving, approveAndSendReports } = useApproveReports();

  const { ecomodClasses, isLoading: isLoadingEcomodClasses } =
    useEcomodClasses();

  const { supClasses, isLoading: isLoadingSupClasses } = useSupClasses();

  const {
    consumerData,
    prevYearConsumerData,
    businessData,
    prevYearBusinessData,
    supData,
    prevYearSupData,
    totalCost,
  } = useSummaryData({
    reportData,
    prevYearReport,
    ecomodClasses,
    supClasses,
    pricesData,
    locale,
  });

  const currentYearReportingCycle = getReportingCycle(reportData as Report);

  const prevYearReportingCycle = getReportingCycle(prevYearReport as Report);

  const approveAndSend = () => {
    const unsentReports = reportsData?.filter(
      (report) => report.reportStatus === ReportStatus.WaitingForApproval,
    );

    if (unsentReports && unsentReports.length > 0) {
      approveAndSendReports(unsentReports);
    }
  };

  const allSent = reportsData?.every(
    (report) => report.reportStatus === ReportStatus.Sent,
  );

  const isLoading =
    isLoadingReports ||
    isLoadingPrevYearReports ||
    isLoadingEcomodClasses ||
    isLoadingSupClasses ||
    isLoadingPrices;

  return (
    <ReportContainer className="report-container">
      <LoadingContainer
        status={
          isLoading ? HttpRequestStatus.LOADING : HttpRequestStatus.SUCCESS
        }
      >
        <ContainerHeader
          title={
            t("header-report") + " " + year + (quarter ? " Q" + quarter : "")
          }
          description={t("header-report-view")}
        />

        <div style={{ maxWidth: "230px", marginTop: "2rem" }}>
          <GoBackLink
            onClick={() => {
              navigate(-1);
            }}
          >
            {`${t("go-back")}` || "← Go back"}
          </GoBackLink>
        </div>

        <TitleComponent
          text={
            t("header-report-summary-of-company", { company: companyName }) +
            " " +
            year +
            (quarter ? " Q" + quarter : "")
          }
        />

        {reportData?.rows.length === 0 && (
          <PaddedContainer>
            <div style={{ paddingLeft: "2rem" }}>
              {t("text-no-data-in-report")}
            </div>
          </PaddedContainer>
        )}
        <PaddedContainer>
          {!!consumerData?.length &&
            consumerData.some((row) => row.combinedWeight > 0) && (
              <SummaryAccordion
                tableData={consumerData || []}
                prevYearData={prevYearConsumerData}
                prevYearReportingCycle={prevYearReportingCycle}
                currentYearReportingCycle={currentYearReportingCycle}
                header={t("label-consumer")}
              />
            )}
          {!!businessData?.length &&
            businessData.some((row) => row.combinedWeight > 0) && (
              <SummaryAccordion
                tableData={businessData || []}
                prevYearData={prevYearBusinessData}
                prevYearReportingCycle={prevYearReportingCycle}
                currentYearReportingCycle={currentYearReportingCycle}
                header={t("label-business")}
              />
            )}
          {!!supData?.length &&
            supData.some((row) => row.combinedWeight > 0) && (
              <SummaryAccordion
                tableData={supData || []}
                prevYearData={prevYearSupData}
                prevYearReportingCycle={prevYearReportingCycle}
                currentYearReportingCycle={currentYearReportingCycle}
                header={t("label-single-use-plastic")}
              />
            )}

          <TotalCostContainer>
            <div>
              {`${t("label-total-cost")}: ${totalCost} ${totalCost ? "€" : ""}`}
              {`${totalCost ? ` ${t("label-vat-amount")}` : ""}`}
            </div>
            <div className="notification">
              {t("label-total-amount-notification")}
            </div>
          </TotalCostContainer>
        </PaddedContainer>

        {!allSent && (
          <StickyActions>
            <Button
              type="button"
              onClick={approveAndSend}
              label={t("cmd-approve-and-send")}
              className="p-ml-2"
              loading={isApproving}
            />
          </StickyActions>
        )}
      </LoadingContainer>
    </ReportContainer>
  );
};

export default CombinedReportView;
